import { useEffect, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabase';
import UserDetailModal, { type UserDetail } from './components/UserDetailModal';

interface AdminUser {
  id: string;
  username: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  trading_code: string;
  two_factor_enabled: boolean;
  created_at: string;
  balance: number;
  balance_locked: number;
  total_profit_loss: number;
  kyc_status: string;
  kyc_id_type: string | null;
  kyc_id_number: string | null;
  kyc_full_name_on_id: string | null;
  last_login: string | null;
}

const formatShortDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatMoney = (n: number) => n.toLocaleString('vi-VN') + 'đ';

const EMPTY_KYC = {
  status: 'none',
  id_type: null,
  id_number: null,
  full_name_on_id: null,
  date_of_birth: null,
  id_front_image: null,
  id_back_image: null,
  selfie_image: null,
  submitted_at: null,
  reviewed_at: null,
  rejection_reason: null,
};

const normalizeDetail = (data: UserDetail | null): UserDetail | null => {
  if (!data) return null;
  return {
    ...data,
    kyc: data.kyc ?? EMPTY_KYC,
    bank_accounts: data.bank_accounts ?? [],
    orders: data.orders ?? [],
    login_history: data.login_history ?? [],
    deposit_requests: data.deposit_requests ?? [],
    withdraw_requests: data.withdraw_requests ?? [],
  };
};

export default function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('list_admin_users');
      if (rpcError) throw rpcError;
      setUsers((data as AdminUser[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải danh sách.');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (user: AdminUser) => {
    setSelectedUser(user);
    setDetailLoading(true);
    setDetail(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('get_admin_user_detail', { p_user_id: user.id });
      if (rpcError) throw rpcError;
      setDetail(normalizeDetail(data as UserDetail));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải chi tiết.');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!selectedUser) return;
    setDetailLoading(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('get_admin_user_detail', { p_user_id: selectedUser.id });
      if (rpcError) throw rpcError;
      setDetail(normalizeDetail(data as UserDetail));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải chi tiết.');
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleActive = async () => {
    if (!selectedUser) return;
    setToggleLoading(true);
    try {
      const { error: rpcError } = await supabase.rpc('update_user_status', {
        p_user_id: selectedUser.id,
        p_is_active: !selectedUser.is_active,
      });
      if (rpcError) throw rpcError;
      setUsers((prev) => prev.map((u) => (u.id === selectedUser.id ? { ...u, is_active: !u.is_active } : u)));
      setSelectedUser({ ...selectedUser, is_active: !selectedUser.is_active });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể cập nhật trạng thái.');
    } finally {
      setToggleLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = users.filter(
    (u) =>
      (u.full_name || u.username || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.phone || '').includes(search)
  );

  const initials = (name: string) =>
    name
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();

  const kycMeta: Record<string, { label: string; cls: string }> = {
    verified: { label: 'Đã xác minh', cls: 'bg-emerald-500/15 text-emerald-400' },
    pending: { label: 'Chờ duyệt', cls: 'bg-accent-500/15 text-accent-400' },
    rejected: { label: 'Từ chối', cls: 'bg-rose-500/15 text-rose-400' },
    none: { label: 'Chưa gửi', cls: 'bg-secondary-500/15 text-secondary-400' },
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground-950">Quản lý người dùng</h1>
            <p className="text-foreground-600 text-sm mt-1">Tổng {users.length} người dùng đã đăng ký</p>
          </div>
          <div className="relative w-full sm:w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo tên, email hoặc SĐT..."
              className="w-full pl-9 pr-4 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-400 text-lg"></i>
            <p className="text-sm text-rose-400">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-sm text-foreground-600">Đang tải...</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-background-200/60">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Người dùng</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Số dư</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">KYC</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Trạng thái</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Đăng ký</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Đăng nhập cuối</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-foreground-600">
                      Không tìm thấy người dùng.
                    </td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr key={u.id} className="border-t border-background-200/40 hover:bg-background-100/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center shrink-0">
                            <span className="text-sm font-bold text-primary-400">{initials(u.full_name || u.username)}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground-950 whitespace-nowrap">{u.full_name || u.username}</p>
                            <p className="text-xs text-foreground-600">{u.email || '—'}</p>
                            <p className="text-[10px] text-foreground-500">SĐT: {u.phone || '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">{formatMoney(Number(u.balance))}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${kycMeta[u.kyc_status]?.cls || 'bg-secondary-500/15 text-secondary-400'}`}>
                          {kycMeta[u.kyc_status]?.label || u.kyc_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${u.is_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
                          {u.is_active ? 'Hoạt động' : 'Đã khóa'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{formatShortDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{formatShortDate(u.last_login)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => loadDetail(u)}
                          className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-700 hover:border-primary-500 hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors"
                        >
                          Xem chi tiết
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          detail={detail}
          detailLoading={detailLoading}
          onClose={() => setSelectedUser(null)}
          onToggleActive={toggleActive}
          toggleLoading={toggleLoading}
          onRefresh={refreshDetail}
        />
      )}
    </AdminLayout>
  );
}